import {useEffect, useState} from 'react'
import {useForm, FormProvider} from 'react-hook-form'
import {Link as RouterLink, useNavigate} from 'react-router-dom'

import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import Loader from '../shared/Loader'
import Form from '../shared/Form'
import Input from '../shared/Input'

import {connect} from '../../state/hooks/tools'
import {PASSWORDS, RECOVERY_REQUEST} from '../../state/actionTypes'
import {readEndpoint, updateResource} from '../../lib/asyncMethods'
import {findRecordByAttribute} from '../../lib/data'
import {isEmpty} from '../../lib/tools'
import {isRecoveryInProgress, isRecoveryOpen} from '../../lib/recovery'

import './passwordManagement.scss'

const PasswordManagement = props => {
  const methods = useForm()
  const navigate = useNavigate()
  const {errors} = methods.formState
  // The change rewraps the private key with the new password, which an open recovery is precisely
  // the state where it cannot. Asked for up front so the form is never offered, and caught on the
  // way back too, for a recovery that opens while this page is sitting open in another tab.
  const [refused, setRefused] = useState(false)
  const blocked = refused || isRecoveryOpen(props.recovery)

  useEffect(() => {
    if (isEmpty(props.passwords) && !props.loading)
      props.readEndpoint('passwords')
  }, [props.passwords, props.loading])

  useEffect(() => {
    props.fetchRecovery('recovery-requests')
  }, [])

  const goBack = () => {
    navigate(-1)
  }

  const handleSubmit = data => {
    props.updateResource({endpoint: `passwords/${props.password.id}`, attributes: data}).then(response => {
      if (response.success)
        methods.reset()
      else if (isRecoveryInProgress(response))
        setRefused(true)
    })
  }

  return (
    <Loader loading={isEmpty(props.currentUser)}>
      <Box component='div' className='password-management' sx={{width: {xs: '100%', md: '80%'}}}>
        <Typography variant='h4' gutterBottom>Password Management</Typography>
        {
          blocked ? (
            <Alert className='gutter-large' severity='warning'>
              <AlertTitle>You can't change your password yet</AlertTitle>
              <Typography variant='body2' gutterBottom>
                Your account is in the middle of a recovery. Finish that first and you'll be able to
                change your password again.
              </Typography>
              <Button className='action' component={RouterLink} to='/dashboard/recovery' size='small'>
                Go to recovery
              </Button>
            </Alert>
          ) : (
              <FormProvider {...methods}>
                <Form onSubmit={handleSubmit}>
                  <Grid className='gutter-large' container rowSpacing={1} columnSpacing={{xs: 1, sm: 2, md: 3}}>
                    <Grid size={{xs: 12, sm: 6}}>
                      <Paper elevation={0} component='div' sx={{bgcolor: 'Background', padding: '8px'}}>
                        <Input
                          fullWidth
                          error={!!errors.currentPassword}
                          label='Current Password'
                          name='currentPassword'
                          type='password'
                          options={{required: 'Password is required'}}
                          helperText={errors.currentPassword?.message}
                        />
                      </Paper>
                    </Grid>
                    <Grid size={{xs: 12, sm: 6}}>
                      <Paper elevation={0} component='div' sx={{bgcolor: 'Background', padding: '8px'}}>
                        <Input
                          fullWidth
                          error={!!errors.password}
                          label='New Password'
                          name='password'
                          type='password'
                          options={{required: 'New Password is required'}}
                          helperText={errors.password?.message}
                        />
                      </Paper>
                    </Grid>
                    <Grid size={{xs: 12, sm: 6}}>
                      <Paper elevation={0} component='div' sx={{bgcolor: 'Background', padding: '8px'}}>
                        <Input
                          fullWidth
                          error={!!errors.confirmPassword}
                          label='Confirm Password'
                          name='confirmPassword'
                          type='password'
                          options={{
                            required: 'Confirm Password is required',
                            validate: value => (value === methods.getValues('password') || 'Passwords do not match')
                          }}
                          helperText={errors.confirmPassword?.message}
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                </Form>
              </FormProvider>
          )
        }
        <Divider />
        <Stack sx={{marginTop: '10px', flexDirection: 'row', justifyContent: 'space-between'}}>
          <Button onClick={goBack} sx={{bgcolor: 'Background'}}>
            <ArrowBackIcon />
            <Typography variant='span'>Back</Typography>
          </Button>
          {
            !blocked && (
              <Button disabled={props.loading} type='submit' form='form' sx={{bgcolor: 'Background'}}>
                <Typography variant='span'>Update Password</Typography>
              </Button>
            )
          }
        </Stack>
      </Box>
    </Loader>
  )
}

const mapStateToProps = ({passwords, users, recoveryRequest}) => ({
  password: findRecordByAttribute({records: passwords.passwords, attribute: 'userId', id: users.currentUser?.id}),
  ...users,
  ...passwords,
  recovery: (recoveryRequest.recoveryRequest || [])[0] || {}
})

const actionCreators = ({passwords, recoveryRequest}) => ({
  readEndpoint: readEndpoint(passwords, PASSWORDS),
  updateResource: updateResource(passwords, PASSWORDS),
  fetchRecovery: readEndpoint(recoveryRequest, RECOVERY_REQUEST)
})

export default connect(mapStateToProps, actionCreators)(PasswordManagement)
